import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Lock, Send, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useApp } from "../store";
import { api } from "../api";

const inputCls =
  "w-full bg-white/[0.05] border border-[var(--accent-soft)]/15 focus:border-[var(--accent)] focus:outline-none px-4 py-3.5 text-sm text-white placeholder:text-white/35 transition-colors duration-300";

export const Contact = () => {
  const { c, t } = useApp();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("form_success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t("form_error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative text-white grain overflow-hidden" style={{ background: "var(--sec-contact-bg, #101E3D)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 relative z-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <Reveal>
          <Eyebrow>{t("contact_eyebrow")}</Eyebrow>
          <h2 data-testid="contact-title" className="mt-5 font-display font-semibold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
            {c("cta_title")}
          </h2>
          <p data-testid="contact-text" className="mt-5 text-base text-white/65 leading-relaxed max-w-md">
            {c("cta_text")}
          </p>

          <div className="mt-10 flex flex-col gap-5">
            <a data-testid="contact-email-link" href={`mailto:${c("contact_email")}`} className="group flex items-center gap-4 text-white/75 hover:text-white transition-colors duration-300">
              <span className="w-11 h-11 grid place-items-center border border-[var(--accent-soft)]/15 group-hover:border-[var(--accent)] text-[var(--accent-soft)] transition-colors duration-300">
                <Mail size={18} />
              </span>
              <span className="text-sm break-all">{c("contact_email")}</span>
            </a>
            <a data-testid="contact-phone-link" href={`tel:${c("contact_phone").replace(/\s/g, "")}`} className="group flex items-center gap-4 text-white/75 hover:text-white transition-colors duration-300">
              <span className="w-11 h-11 grid place-items-center border border-[var(--accent-soft)]/15 group-hover:border-[var(--accent)] text-[var(--accent-soft)] transition-colors duration-300">
                <Phone size={18} />
              </span>
              <span className="text-sm">{c("contact_phone")}</span>
            </a>
            <div data-testid="contact-address" className="flex items-center gap-4 text-white/75">
              <span className="w-11 h-11 shrink-0 grid place-items-center border border-[var(--accent-soft)]/15 text-[var(--accent-soft)]">
                <MapPin size={18} />
              </span>
              <span className="text-sm">{c("contact_address")}</span>
            </div>
          </div>

          {c("contact_image") && (
            <div className="relative mt-10 max-w-md">
              <div className="absolute -top-3 -left-3 w-full h-full border border-[var(--accent)]/40 pointer-events-none" />
              <img
                src={c("contact_image")}
                alt="Contact — Polynésie Gestion et Projet"
                data-testid="contact-side-image"
                loading="lazy"
                className="relative w-full h-52 object-cover"
              />
            </div>
          )}

          <p className="mt-10 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/40">
            <Lock size={12} className="text-[var(--accent-soft)]" />
            {t("form_note")}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white/[0.06] border border-[var(--accent-soft)]/10 shadow-sm p-7 sm:p-9 flex flex-col gap-5"
          >
            <div>
              <label htmlFor="contact-name" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("form_name")} *
              </label>
              <input
                id="contact-name"
                data-testid="contact-form-name-input"
                required
                minLength={2}
                maxLength={100}
                value={form.name}
                onChange={set("name")}
                className={inputCls}
                placeholder="Māeva Tahiti"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("form_email")} *
              </label>
              <input
                id="contact-email"
                data-testid="contact-form-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                placeholder="vous@exemple.pf"
              />
            </div>
            <div>
              <label htmlFor="contact-subject" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("form_subject")}
              </label>
              <input
                id="contact-subject"
                data-testid="contact-form-subject-input"
                maxLength={150}
                value={form.subject}
                onChange={set("subject")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("form_message")} *
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-form-message-input"
                required
                minLength={10}
                maxLength={5000}
                rows={5}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={sending}
              className="group mt-2 inline-flex items-center justify-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
              {sending ? t("form_sending") : t("form_send")}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
